import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { clientsService } from './clients.service';
import { handleThunkError } from '../helpers/thunkError';

const initialState = {
  clients: [],
  loadingClients: false,

  selectedClient: null,

  pageIndex: 0,
  totalPages: 0,
  totalCount: 0,
  pageSize: 0,

  clientAccounts: [],
  loadingClientAccounts: false,

  clientAccountFunds: [],
  loadingClientAccountFunds: false,

  accountHistoricBalances: [],
  loadingAccountHistoricBalances: false,

  portfolioHistoricBalances: [],
  loadingPortfolioHistoricBalances: false,

  error: null
};

// Reusable mapper for paginated responses
const applyClientPayload = (state, payload) => {
  state.clients = payload?.clients || [];
  state.pageIndex = payload?.pageIndex ?? 0;
  state.totalPages = payload?.totalPages ?? 0;
  state.totalCount = payload?.length ?? 0;
  state.pageSize = payload?.pageSize ?? 0;
};

// --- GET CLIENTS ---
export const getClients = createAsyncThunk('clients/getClients', async (filter = {}, thunkAPI) => {
  try {
    return await clientsService.getClients(filter);
  } catch (error) {
    return handleThunkError(error, thunkAPI, 'Failed to fetch clients');
  }
});

// --- GET PRACTICE CLIENTS ---
export const getPracticeClients = createAsyncThunk(
  'clients/getPracticeClients',
  async (filter = {}, thunkAPI) => {
    try {
      return await clientsService.getPracticeClients(filter);
    } catch (error) {
      return handleThunkError(error, thunkAPI, 'Failed to fetch clients');
    }
  }
);

// --- GET ADVISOR CLIENTS ---
export const getAdvisorClients = createAsyncThunk(
  'clients/getAdvisorClients',
  async (filter = {}, thunkAPI) => {
    try {
      return await clientsService.getAdvisorClients(filter);
    } catch (error) {
      return handleThunkError(error, thunkAPI, 'Failed to fetch advisor clients');
    }
  }
);

// --- GET CLIENT ACCOUNTS ---
export const getClientAccounts = createAsyncThunk(
  'clients/getClientAccounts',
  async ({ id, asof }, thunkAPI) => {
    try {
      return await clientsService.getClientAccounts(id, asof);
    } catch (error) {
      return handleThunkError(error, thunkAPI, 'Failed to fetch client accounts');
    }
  }
);

// --- GET CLIENT ACCOUNT FUNDS ---
export const getClientAccountFunds = createAsyncThunk(
  'clients/getClientAccountFunds',
  async ({ id, asof }, thunkAPI) => {
    try {
      return await clientsService.getClientAccountFunds({ id, asof });
    } catch (error) {
      return handleThunkError(error, thunkAPI, 'Failed to fetch client account funds');
    }
  }
);

// --- GET ACCOUNT HISTORIC BALANCES ---
export const getAccountHistoricBalances = createAsyncThunk(
  'clients/getAccountHistoricBalances',
  async ({ accountIds, fromDate, toDate }, thunkAPI) => {
    try {
      return await clientsService.getAccountHistoricBalances({
        accountIds,
        fromDate,
        toDate
      });
    } catch (error) {
      return handleThunkError(error, thunkAPI, 'Failed to fetch account historic balances');
    }
  }
);

// --- GET PORTFOLIO HISTORIC BALANCES ---
export const getPortfolioHistoricBalances = createAsyncThunk(
  'clients/getPortfolioHistoricBalances',
  async ({ clientId, fromDate, toDate }, thunkAPI) => {
    try {
      return await clientsService.getPortfolioHistoricBalances({
        clientId,
        fromDate,
        toDate
      });
    } catch (error) {
      return handleThunkError(error, thunkAPI, 'Failed to fetch portfolio historic balances');
    }
  }
);

export const clientsSlice = createSlice({
  name: 'clients',
  initialState,

  reducers: {
    setSelectedClient: (state, action) => {
      state.selectedClient = action.payload ?? null;
    },

    clearSelectedClient: (state) => {
      state.selectedClient = null;
    },

    clearClients: (state) => {
      state.clients = [];
      state.pageIndex = 0;
      state.totalPages = 0;
      state.totalCount = 0;
      state.pageSize = 0;
    },

    clearClientAccounts: (state) => {
      state.clientAccounts = [];
    },

    clearClientAccountFunds: (state) => {
      state.clientAccountFunds = [];
    },

    clearAccountHistoricBalances: (state) => {
      state.accountHistoricBalances = [];
    },
    clearPortfolioHistoricBalances: (state) => {
      state.portfolioHistoricBalances = [];
    }
  },

  extraReducers: (builder) => {
    builder

      // --- GET CLIENTS ---
      .addCase(getClients.pending, (state) => {
        state.loadingClients = true;
        state.error = null;
      })
      .addCase(getClients.fulfilled, (state, action) => {
        state.loadingClients = false;
        applyClientPayload(state, action.payload);
      })
      .addCase(getClients.rejected, (state, action) => {
        state.loadingClients = false;
        state.error = action.payload;
      })

      // --- GET PRACTICE CLIENTS ---
      .addCase(getPracticeClients.pending, (state) => {
        state.loadingClients = true;
        state.error = null;
      })
      .addCase(getPracticeClients.fulfilled, (state, action) => {
        state.loadingClients = false;
        applyClientPayload(state, action.payload);
      })
      .addCase(getPracticeClients.rejected, (state, action) => {
        state.loadingClients = false;
        state.error = action.payload;
      })

      // --- GET ADVISOR CLIENTS ---
      .addCase(getAdvisorClients.pending, (state) => {
        state.loadingClients = true;
        state.error = null;
      })
      .addCase(getAdvisorClients.fulfilled, (state, action) => {
        state.loadingClients = false;
        applyClientPayload(state, action.payload);
      })
      .addCase(getAdvisorClients.rejected, (state, action) => {
        state.loadingClients = false;
        state.error = action.payload;
      })

      // --- GET CLIENT ACCOUNTS ---
      .addCase(getClientAccounts.pending, (state) => {
        state.loadingClientAccounts = true;
        state.error = null;
      })
      .addCase(getClientAccounts.fulfilled, (state, action) => {
        state.loadingClientAccounts = false;
        state.clientAccounts = action.payload;
      })
      .addCase(getClientAccounts.rejected, (state, action) => {
        state.loadingClientAccounts = false;
        state.error = action.payload;
      })

      // --- GET CLIENT ACCOUNT FUNDS ---
      .addCase(getClientAccountFunds.pending, (state) => {
        state.loadingClientAccountFunds = true;
        state.error = null;
      })
      .addCase(getClientAccountFunds.fulfilled, (state, action) => {
        state.loadingClientAccountFunds = false;
        state.clientAccountFunds = action.payload;
      })
      .addCase(getClientAccountFunds.rejected, (state, action) => {
        state.loadingClientAccountFunds = false;
        state.error = action.payload;
      })

      // --- GET ACCOUNT HISTORIC BALANCES ---
      .addCase(getAccountHistoricBalances.pending, (state) => {
        state.loadingAccountHistoricBalances = true;
        state.error = null;
      })
      .addCase(getAccountHistoricBalances.fulfilled, (state, action) => {
        state.loadingAccountHistoricBalances = false;
        state.accountHistoricBalances = action.payload;
      })
      .addCase(getAccountHistoricBalances.rejected, (state, action) => {
        state.loadingAccountHistoricBalances = false;
        state.error = action.payload;
      })

      // --- GET PORTFOLIO HISTORIC BALANCES ---
      .addCase(getPortfolioHistoricBalances.pending, (state) => {
        state.loadingPortfolioHistoricBalances = true;
        state.error = null;
      })
      .addCase(getPortfolioHistoricBalances.fulfilled, (state, action) => {
        state.loadingPortfolioHistoricBalances = false;
        state.portfolioHistoricBalances = action.payload;
      })
      .addCase(getPortfolioHistoricBalances.rejected, (state, action) => {
        state.loadingPortfolioHistoricBalances = false;
        state.error = action.payload;
      });
  }
});

export const {
  setSelectedClient,
  clearSelectedClient,
  clearClients,
  clearClientAccounts,
  clearClientAccountFunds,
  clearAccountHistoricBalances,
  clearPortfolioHistoricBalances
} = clientsSlice.actions;

export default clientsSlice.reducer;
